/**
 * KernelChat — embedded conversational editor for a single kernel.
 *
 * Shares the kernel draft with KernelEditor via parent state
 * (draft + setDraft come in as props). Agent tool calls mutate the
 * same draft, so the user sees changes appear in the editor's other
 * tabs as the conversation progresses.
 *
 * Intentionally lean compared to ChatConsole: no minimap, no
 * persistence, no slash commands, no image staging, no audit. Each
 * page mount opens a fresh conversation. The agent has the kernel
 * editing tool family (lib/kernelEditTools.js), the analytics tools
 * inherited from createChatAgent, and a system prompt suffix that
 * tells it to focus on editing this specific kernel.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createChatAgent } from "../lib/piChat.js";
import { createKernelEditTools } from "../lib/kernelEditTools.js";
import { makeLoopBreaker } from "../lib/loopBreaker.js";
import { defaultThinkingLevelFor } from "../lib/modelCatalog.js";
import { KNOWN_RENDER_KINDS } from "../lib/kernelStore.js";
import Markdown from "./Markdown.jsx";
import ScrollArea from "./ScrollArea.jsx";
import { NOTE_CLASS } from "../lib/ui.js";
import { reduceChatEvent, visibleChatTurns, settleOpenTurns } from "../lib/leanChatTurns.js";

// Kernel-editor system-prompt suffix. The agent inherits the full
// main-chat system prompt (analytics_query, render_block, schema
// discovery, sub-agent delegation, python, …) and we APPEND this
// block to scope it to editing one saved kernel. Keeping the main
// prompt intact means the agent builds with the SAME flow it's
// fluent at in the main chat: do the analysis, finish with
// render_block, then `commit_to_kernel` crystallises the transcript
// into the kernel via the dedicated extractor (the same one the main
// chat's /save-kernel uses). It does NOT hand-assemble steps — that
// inline transcription was the source of the brittleness.
//
// The current kernel state is embedded directly into the suffix so
// the agent never needs a "read state" tool. Edit tools return the
// updated state in their results, keeping the context current — same
// as the main chat, where state lives in the conversation, not a tool.
function summariseForPrompt(kernel) {
    return {
        command: kernel.command,
        title: kernel.title || null,
        description: kernel.description || null,
        params: kernel.params || null,
        steps: (kernel.steps || []).map((s) => ({
            id: s.id,
            kind: s.kind,
            ...(s.kind === "analytics_query" ? { spec: s.spec } : {}),
            ...(s.kind === "python"
                ? {
                    code: s.code,
                    inputs: s.inputs || {},
                    packages: s.packages || [],
                }
                : {}),
        })),
        render: kernel.render || null,
        renderSource: kernel.renderSource || null,
    };
}

function buildKernelEditSuffix(kernel) {
    const renderKinds = [...KNOWN_RENDER_KINDS].join(", ");
    const stateJson = JSON.stringify(summariseForPrompt(kernel), null, 2);
    const empty = !Array.isArray(kernel.steps) || kernel.steps.length === 0;
    return [
        `# Kernel editor mode`,
        ``,
        `You're editing one saved kernel: \`/${kernel.command}\`. A kernel is a deterministic recipe — ordered steps (\`analytics_query\` specs or \`python\` code) + a render spec — that re-runs outside the chat to reproduce an analysis.`,
        ``,
        `## How to BUILD or REBUILD a kernel (the main case)`,
        ``,
        `Do the analysis EXACTLY like you would in the main chat, then crystallise it. Do NOT hand-assemble steps.`,
        ``,
        `1. Run the analysis with your normal tools: \`analytics_query\`, \`execute_python\`, \`schema_search\`, \`schema_describe_entity\`. Iterate until the data is right.`,
        `2. Finish with a \`render_block\` on the final result — pick the visual that fits (kinds: ${renderKinds}). This is exactly the main-chat flow.`,
        `3. Call \`commit_to_kernel\`. It reads your transcript and crystallises the successful query/transform/render into this kernel's steps via a dedicated extractor with its own validation loop. The draft updates automatically.`,
        `4. (Optional) \`run_kernel\` to confirm the committed kernel reproduces the result, then reply in 1-2 sentences.`,
        ``,
        `Think of \`commit_to_kernel\` as the kernel-editor equivalent of finishing an analysis: you do the work, then commit it. You do NOT manually copy specs into \`add_step\`.`,
        ``,
        `## How to make a SMALL TWEAK to an already-built kernel`,
        ``,
        `If the kernel already has steps and the user just wants a tweak (swap render kind, rename a step, change a literal), skip the analysis and edit directly: \`update_render\`, \`update_render_source\`, \`update_step\`, \`update_meta\`, \`add_step\`, \`remove_step\`, \`move_step\`. Then \`run_kernel\` to verify. Don't re-run a whole analysis for a one-line change.`,
        ``,
        `## Current kernel state (live snapshot — refreshed in every edit tool result)`,
        ``,
        '```json',
        stateJson,
        '```',
        ``,
        empty
            ? `This kernel is empty — build it from scratch using the BUILD flow above. The state above IS current; there is no "read state" tool and you never need one.`
            : `The state above is current. Every edit tool returns the new state in its result, so your context always has the latest — there is no "read state" tool and you never need one. Decide: is this a rebuild (use the BUILD flow + commit_to_kernel) or a small tweak (edit directly)?`,
        ``,
        `## Notes`,
        ``,
        `- Edit tools mutate an in-progress DRAFT shown next to this chat. The user reviews and clicks Save — there is no save tool. Don't tell the user to "save" unless they ask.`,
        `- Do NOT use \`canvas_*\` or \`suggest_kernel\` here — they're for ad-hoc chats. \`render_block\` IS used (step 2 above) as the input \`commit_to_kernel\` reads.`,
        `- Keep responses tight — narrow panel. Skip preamble; act and report briefly.`,
    ].filter(Boolean).join("\n");
}

// ── Render helpers ───────────────────────────────────────────────

function ToolCallRow({ tool }) {
    const dotColor =
        tool.status === "done" ? "bg-[color:var(--green)]"
        : tool.status === "error" ? "bg-[color:var(--red,#c0392b)]"
        : tool.status === "running" ? "bg-[color:var(--ink)] animate-pulse"
        : "bg-[color:var(--muted-soft)]";
    return (
        <div className="flex min-w-0 items-baseline gap-[6px] py-[2px] text-[10.5px] leading-[1.3]">
            <span className={`relative top-[1px] inline-block h-[6px] w-[6px] shrink-0 rounded-full ${dotColor}`} aria-hidden="true" />
            <span className="shrink-0 font-mono text-[color:var(--ink)]">{tool.name}</span>
            {tool.result ? (
                <span className="min-w-0 flex-1 truncate text-[color:var(--muted)]" title={tool.result}>
                    {tool.result.replace(/\n/g, " ")}
                </span>
            ) : null}
        </div>
    );
}

function UserBubble({ text }) {
    return (
        <div className="grid min-w-0 gap-[3px]">
            <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>You</span>
            <p className="m-0 text-[12px] leading-[1.45] text-[color:var(--ink)] whitespace-pre-wrap [overflow-wrap:anywhere]">{text}</p>
        </div>
    );
}

function AgentBubble({ turn }) {
    return (
        <div className="grid min-w-0 gap-[6px]">
            <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>
                Agent {turn.status === "running" ? "· thinking…" : turn.tools.length ? `· ${turn.tools.length} tool${turn.tools.length === 1 ? "" : "s"}` : ""}
            </span>
            {turn.tools.length ? (
                <div className="grid min-w-0 gap-[1px] rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--bg)] px-[8px] py-[6px]">
                    {turn.tools.map((tc, i) => <ToolCallRow key={tc.id || tc.contentIndex || i} tool={tc} />)}
                </div>
            ) : null}
            {turn.text ? (
                <div className="min-w-0 text-[12px] leading-[1.5] text-[color:var(--ink)] [overflow-wrap:anywhere]">
                    <Markdown text={turn.text} />
                </div>
            ) : turn.status === "running" ? (
                <span className="text-[11px] text-[color:var(--muted-soft)]">…</span>
            ) : null}
            {turn.error ? (
                <span className="text-[11px] text-[color:var(--red,#c0392b)]">⚠ {turn.error}</span>
            ) : null}
        </div>
    );
}

// ── Main component ───────────────────────────────────────────────

export default function KernelChat({
    kernel,
    draft,
    setDraft,
    resolvedDefinition = null,
    runContext = null,
}) {
    // The agent's tools read from a getDraft thunk so they always
    // see the latest state, even if React state batched since the
    // last call.
    const draftRef = useRef(draft);
    useEffect(() => { draftRef.current = draft; }, [draft]);
    const getDraft = useCallback(() => draftRef.current, []);
    // The set thunk routes through React so the editor updates in real time.
    const setDraftRef = useRef(setDraft);
    useEffect(() => { setDraftRef.current = setDraft; }, [setDraft]);
    const setDraftThunk = useCallback((nextDraft) => {
        setDraftRef.current(nextDraft);
        draftRef.current = nextDraft;
    }, []);

    const [agent, setAgent] = useState(null);
    const [agentError, setAgentError] = useState(null);
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [input, setInput] = useState("");
    const offRef = useRef(null);
    // Live handle to the agent so commit_to_kernel can read the
    // current transcript (agent.state.messages) at execute time —
    // the tool is built before the agent exists, so it reads lazily.
    const agentRef = useRef(null);
    const getMessages = useCallback(() => agentRef.current?.state?.messages || [], []);

    // Build the agent once per kernel.id. The tools' getDraft closes
    // over draftRef, so they always see the latest draft regardless
    // of when the agent was created.
    useEffect(() => {
        let cancelled = false;
        let agentInstance = null;
        (async () => {
            try {
                const editTools = createKernelEditTools({
                    getDraft,
                    setDraft: setDraftThunk,
                    getMessages,
                    resolvedDefinition,
                    runContext,
                });
                // Inherit the full main-chat toolkit (analytics_query,
                // execute_python, render_block, schema_*, spawn_*, …)
                // and APPEND the kernel-edit tools. The agent does the
                // analysis EXACTLY like the main chat (ending in
                // render_block), then calls commit_to_kernel to
                // crystallise the transcript into the draft via the
                // dedicated extraction sub-agent. The suffix scopes it
                // to this one kernel.
                agentInstance = await createChatAgent({
                    chatId: `kernel-edit:${kernel.command}`,
                    extraTools: editTools,
                    systemPromptSuffix: buildKernelEditSuffix(kernel),
                    // Safety net: hard-break runaway loops. Once a
                    // model starts re-issuing the identical tool call,
                    // prompting can't pull it out — it pattern-matches
                    // its own history. Terminate after 3 consecutive
                    // calls with the same tool AND args so the user
                    // regains control. The same tool with different args
                    // (kernel building fires many analytics_query calls
                    // in a row) resets the counter, so legitimate
                    // exploration is never cut off.
                    afterToolCall: makeLoopBreaker(),
                });
                if (cancelled) return;
                // The editor has no thinking-depth picker, and createChatAgent
                // sets no default — so without this the editor chat ran with
                // thinking unset, which pi emits as `thinking: {type:
                // "disabled"}` and Fable rejects with a 400. Apply the model's
                // default depth, exactly as the main chat does (medium on a
                // flagship reasoning model, off otherwise).
                agentInstance.state.thinkingLevel = defaultThinkingLevelFor(
                    agentInstance.state.model?.provider,
                    agentInstance.state.model?.id,
                );
                agentRef.current = agentInstance;
                offRef.current = agentInstance.subscribe((event) => {
                    setItems((prev) => reduceChatEvent(prev, event));
                });
                setAgent(agentInstance);
            } catch (err) {
                if (!cancelled) setAgentError(err?.message || String(err));
            }
        })();
        return () => {
            cancelled = true;
            try { offRef.current?.(); } catch { /* noop */ }
            offRef.current = null;
            agentRef.current = null;
            setItems([]);
            setAgent(null);
            setBusy(false);
        };
    }, [
        kernel.command,
        getDraft,
        setDraftThunk,
        getMessages,
        resolvedDefinition,
        runContext,
    ]);

    const onSend = useCallback(async () => {
        const text = input.trim();
        if (!text || !agent || busy) return;
        setInput("");
        setItems((prev) => [...prev, { kind: "user", id: `u:${prev.length}`, text }]);
        setBusy(true);
        try {
            await agent.prompt(text);
        } catch (err) {
            if (err?.name !== "AbortError") {
                setItems((prev) => [...prev, { kind: "user", id: `err:${prev.length}`, text: `⚠ ${err?.message || String(err)}` }]);
            }
        } finally {
            // Sweep any "running" turns that never got their turn_end /
            // agent_end, so the empty-turn filter can drop them and no
            // dangling AGENT header survives the loop.
            setItems(settleOpenTurns);
            setBusy(false);
        }
    }, [input, agent, busy]);

    const onKeyDown = useCallback((e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSend();
        }
    }, [onSend]);

    // Auto-scroll the transcript to the latest message whenever items
    // changes — simple and reliable for a short chat panel.
    const transcriptRef = useRef(null);
    useEffect(() => {
        const el = transcriptRef.current;
        if (!el) return;
        const viewport = el.querySelector("[data-overlayscrollbars-viewport]") || el.querySelector(".os-viewport") || el;
        viewport.scrollTop = viewport.scrollHeight;
    }, [items]);

    const sendDisabled = !agent || busy || !input.trim();

    const visibleItems = visibleChatTurns(items);

    return (
        <div className="flex h-full min-h-0 min-w-0 flex-col">
            {agentError ? (
                <div className="mt-[10px] rounded-[2px] border border-[color:var(--red,#c0392b)] bg-[color:var(--bg)] px-[10px] py-[8px] text-[11px] text-[color:var(--red,#c0392b)]">
                    Chat failed to initialise: {agentError}
                </div>
            ) : null}
            <div ref={transcriptRef} className="flex-1 min-h-0 min-w-0">
                <ScrollArea
                    data-content-scroll
                    className="min-h-0 h-full"
                    contentClassName="grid min-w-0 gap-[14px] pt-[14px] pb-[12px]"
                >
                    {visibleItems.length === 0 ? (
                        <div className="rounded-[2px] border border-dashed border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[12px] py-[10px] text-[11px] leading-[1.5] text-[color:var(--muted)]">
                            Ask the agent to edit <span className="font-mono text-[color:var(--ink)]">/{kernel.command}</span>. It can rename steps, swap the render kind, run the kernel to verify, etc. Try:
                            <ul className="mt-[6px] list-disc pl-[18px]">
                                <li>"Change the render to a bar chart."</li>
                                <li>"Add a python step that filters rows where value &gt; 100."</li>
                                <li>"Run it with days=7 and tell me what came back."</li>
                            </ul>
                        </div>
                    ) : (
                        visibleItems.map((it) => it.kind === "user"
                            ? <UserBubble key={it.id} text={it.text} />
                            : <AgentBubble key={it.id} turn={it} />
                        )
                    )}
                </ScrollArea>
            </div>
            <div className="shrink-0 border-t border-[color:var(--line-soft)] pt-[10px] pb-[18px]">
                <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={agent ? "Ask for a change… (Enter to send, Shift-Enter for newline)" : "Loading agent…"}
                    disabled={!agent || busy}
                    rows={3}
                    className="w-full resize-none border border-[color:var(--line)] bg-[color:var(--bg)] px-[8px] py-[6px] text-[12px] leading-[1.4] text-[color:var(--ink)] outline-none focus:border-[color:var(--line-strong)]"
                />
                <div className="mt-[6px] flex items-baseline gap-[8px]">
                    <button
                        type="button"
                        onClick={onSend}
                        disabled={sendDisabled}
                        className="cursor-pointer rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[10px] py-[3px] text-[10px] uppercase tracking-[0.04em] font-medium text-[color:var(--ink)] hover:border-[color:var(--green)] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {busy ? "Thinking…" : "Send"}
                    </button>
                    <span className={`${NOTE_CLASS} text-[10px]`}>
                        {busy ? "Agent is running tools…" : agent ? "Ready" : "Loading…"}
                    </span>
                </div>
            </div>
        </div>
    );
}
